import BigNumber from 'bignumber.js';
import capitalize from 'lodash/capitalize';
import startCase from 'lodash/startCase';
import formatDecimal from '@paradigm/utils/formatDecimal';
import { getStrategyOptions } from '#/shared/strategies';
import { BaseReq } from '@paradigm/api/src';

export const INSTRUMENT_KINDS_LIQUIDITY = [
  'OPTION',
  'FUTURE',
  'OPTION_FUTURE',
] as const;

export const INSTRUMENT_KINDS_VOLUMES = [
  'OPTION',
  'FUTURE',
  'PERPETUAL',
  'SPOT',
] as const;

export type InstrumentKindVolumes = typeof INSTRUMENT_KINDS_VOLUMES[number];
export type InstrumentKindLiquidity = typeof INSTRUMENT_KINDS_LIQUIDITY[number];

export const PROTOCOLS_VOLUMES = ['RFQ', 'OB', 'D1'] as const;
export const PROTOCOLS = ['RFQ', 'OB'] as const;
export type Protocol =
  | typeof PROTOCOLS[number]
  | typeof PROTOCOLS_VOLUMES[number];

export const VENUES = ['DBT', 'BYB', 'BIT'] as const;
export type Venue = typeof VENUES[number];

export const CURRENCIES = [
  'BTC',
  'ETH',
  'SOL',
  'BCH',
  'TON',
  'EOS',
  'XRP',
] as const;
export type Currency = typeof CURRENCIES[number];

export interface UserFirm {
  readonly name: string;
  readonly desks: readonly string[];
}

const toFormOption =
  (transformLabel?: (v: string) => string) =>
  <T extends string>(value: T) =>
    ({
      value,
      label: transformLabel?.(value) ?? value,
    } as const);

export const STRATEGY_OPTIONS = getStrategyOptions(true);
export const CURRENCY_OPTIONS = CURRENCIES.map(toFormOption());
export const VENUE_OPTIONS = VENUES.map(toFormOption());
export const PROTOCOL_OPTIONS = PROTOCOLS.map(toFormOption());
export const PROTOCOL_OPTIONS_VOLUMES = PROTOCOLS_VOLUMES.map(toFormOption());
export const INSTRUMENT_KINDS_VOLUMES_OPTIONS = INSTRUMENT_KINDS_VOLUMES.map(
  toFormOption(capitalizeAndRemoveUnderscores),
);
export const INSTRUMENT_KINDS_LIQUIDITY_OPTIONS =
  INSTRUMENT_KINDS_LIQUIDITY.map(toFormOption(capitalizeAndRemoveUnderscores));

function capitalizeAndRemoveUnderscores(value: string) {
  return startCase(capitalize(value));
}

export const MOBILE_VIEW_VOLUME_OPTIONS = [
  'Overall',
  'Maker',
  'Taker',
] as const;

export const MOBILE_VIEW_LIQUIDITY_OPTIONS = ['fills', 'responses'] as const;

export const formatBigNumber = (value: BigNumber) => {
  if (value.gt(1_000_000)) {
    const millions = value.dividedBy(1_000_000);

    return `${formatDecimal(millions.decimalPlaces(1).toString())} M`;
  }

  return formatDecimal(value.toString());
};

export interface BaseLeaderboardResp<Result, DateType = number> {
  // Unix epoch of the last time the rank data was updated.
  readonly last_updated_at: DateType;
  readonly user_firm: UserFirm;
  readonly count: number;
  // Array of Objects with details related to each firm. The data is sorted in the descending order by the total_volume.
  readonly results: readonly Result[];
}

export interface BaseLeaderboardReq extends BaseReq {
  /**
   * The currency of the assets to filter the data for.
   * The parameter can contain multiple currencies such as ?currencies=BTC,ETH
   */
  readonly currencies?: readonly Currency[];
  /**
   * The type of contract. Valid values include FUTURE, OPTION, PERPETUAL, SPOT for volumes
   * and FUTURE, OPTION, FUTURE_OPTION for liquidity.
   * The parameter can contain multiple kinds such as ?instrument_kinds=FUTURE,OPTION
   */
  readonly instrumentKinds?:
    | readonly InstrumentKindVolumes[]
    | readonly InstrumentKindLiquidity[];
  /**
   * The liquidity protocol to return data for. Valid values include RFQ, OB, D1.
   * The parameter can contain multiple protocols such as ?protocols=RFQ,OB
   */
  readonly protocols?: readonly Protocol[];
  /**
   * The underlying settlement venue to return data for. Valid values include DBT, BIT, BYB.
   * The parameter can contain multiple venues such as ?venues=DBT,BIT
   */
  readonly venues?: readonly Venue[];
  /**
   * The initial date and time to filter the data. Default all times if not provided.
   */
  readonly start?: number | null;
  /**
   * The end date and time to filter the data. Default now if not provided.
   */
  readonly end?: number | null;
}

export interface BaseLeaderboardEntity {
  readonly isFromUser: boolean;
}
